import React from "react";
import { Dropdown } from "react-bootstrap";
import { Download, Eye, MoreVertical, Calendar, Tag, FileText, FileSpreadsheet, FileImage, File, Play, Clock } from "lucide-react";
import { AppBadge } from "../ui";
import { isViewableType } from "./MaterialViewerModal";
import "./Materials.scss";

const FILE_ICONS = {
  PDF: { icon: FileText, color: "#ef4444" },
  PPT: { icon: FileText, color: "#f97316" },
  PPTX: { icon: FileText, color: "#f97316" },
  DOC: { icon: FileText, color: "#3b82f6" },
  DOCX: { icon: FileText, color: "#3b82f6" },
  XLS: { icon: FileSpreadsheet, color: "#22c55e" },
  XLSX: { icon: FileSpreadsheet, color: "#22c55e" },
  JPG: { icon: FileImage, color: "#8b5cf6" },
  JPEG: { icon: FileImage, color: "#8b5cf6" },
  PNG: { icon: FileImage, color: "#8b5cf6" },
  TXT: { icon: FileText, color: "#6b7280" },
  ZIP: { icon: File, color: "#eab308" },
  RAR: { icon: File, color: "#eab308" },
  MP4: { icon: Play, color: "#6366f1" },
  MOV: { icon: Play, color: "#6366f1" },
  WEBM: { icon: Play, color: "#6366f1" },
  MKV: { icon: Play, color: "#6366f1" },
};

const CATEGORY_LABELS = {
  ppt: "PPT",
  script: "Script",
  notes: "Notes",
  other: "Other",
};

const CATEGORY_COLORS = {
  ppt: "amber",
  script: "blue",
  notes: "emerald",
  other: "zinc",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDuration = (min) => {
  if (!min || min <= 0) return null;
  if (min >= 60) return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
  return `${Math.round(min)}m`;
};

const MaterialCard = ({ material, role, currentUserId, onDownload, onView, onEdit, onDelete, onPlay }) => {
  const isAdmin = role === "admin" || role === "superadmin";
  const isInstructor = role === "instructor";
  const canEdit = isAdmin || isInstructor;
  const canDelete = isAdmin || (isInstructor && (material.uploadedBy?._id || material.uploadedBy) === currentUserId);
  const isReady = material.status === "ready";
  const isProcessing = material.status === "processing";
  const canView = isReady && isViewableType(material.fileType);
  const isVideoMaterial = material.isVideo || ["MP4", "MOV", "WEBM", "MKV"].includes(material.fileType);

  const fileInfo = FILE_ICONS[material.fileType] || { icon: File, color: "#6b7280" };
  const IconComp = fileInfo.icon;

  const handleClick = () => {
    if (isVideoMaterial && onPlay) {
      onPlay(material);
    } else if (canView && onView) {
      onView(material);
    } else if (isReady && onDownload) {
      onDownload(material);
    }
  };

  return (
    <div className="material-card" onClick={handleClick}>
      {/* File Icon */}
      <div className="material-card__icon" style={{ color: fileInfo.color }}>
        <IconComp size={32} />
        <span className="material-card__ext">{material.fileType}</span>
      </div>

      {/* Body */}
      <div className="material-card__body">
        <div className="material-card__title-row">
          <h4 className="material-card__title">{material.title}</h4>

          {(canEdit || canDelete) && (
            <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
              <Dropdown.Toggle as="button" bsPrefix="material-card__menu-btn">
                <MoreVertical size={16} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                renderOnMount
                popperConfig={{
                  strategy: "fixed",
                  modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }],
                }}
              >
                {canEdit && <Dropdown.Item onClick={() => onEdit && onEdit(material)}>Edit</Dropdown.Item>}
                {canDelete && (
                  <Dropdown.Item className="text-danger" onClick={() => onDelete && onDelete(material)}>
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>

        {/* Category badge + preview badge */}
        <div className="material-card__category">
          {material.isPreview && (
            <AppBadge color="emerald" size="sm">Free Preview</AppBadge>
          )}
          {isVideoMaterial && (
            <AppBadge variant="outline" color="violet">Video</AppBadge>
          )}
          {!isVideoMaterial && (
            <AppBadge variant="outline" color={CATEGORY_COLORS[material.category] || "zinc"}>
              {CATEGORY_LABELS[material.category] || material.category}
            </AppBadge>
          )}
          {isProcessing && (
            <AppBadge variant="outline" color="amber">Uploading…</AppBadge>
          )}
        </div>

        {material.topic && (
          <div className="material-card__topic">
            <Tag size={12} />
            {material.topic}
          </div>
        )}

        <div className="material-card__meta">
          <span>
            <Calendar size={12} />
            {formatDate(material.sessionDate)}
          </span>
          {material.fileSize > 0 && <span>{formatSize(material.fileSize)}</span>}
          {isVideoMaterial && formatDuration(material.duration) && (
            <span><Clock size={12} />{formatDuration(material.duration)}</span>
          )}
          {isReady && isVideoMaterial && (
            <span className="material-card__view-hint">
              <Play size={12} />
              Play
            </span>
          )}
          {isReady && canView && !isVideoMaterial && (
            <span className="material-card__view-hint">
              <Eye size={12} />
              View
            </span>
          )}
          {isReady && (
            <span
              className="material-card__download-hint"
              onClick={(e) => { e.stopPropagation(); onDownload?.(material); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onDownload?.(material); } }}
            >
              <Download size={12} />
              Download
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialCard;
